import type { OrderStatus } from "@tilt/pos-sdk";

import { cn } from "../../lib/utils";

// Dark-mode pairs use ``-900/30`` bg + ``-200`` text + matching ring
// - keeps the warn / success / error semantic readable on a dark
// canvas without the light-on-dark "tile floating" effect the
// pre-2026-05-03 base class produced.
const ORDER_TONE: Record<OrderStatus, string> = {
  open: "bg-surface-strong text-foreground ring-line",
  partially_paid:
    "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/40",
  paid: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/40",
  voided: "bg-surface-muted text-muted ring-line",
  partially_refunded:
    "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/40",
  refunded: "bg-surface-muted text-muted ring-line",
};

const PAYMENT_TONE: Record<string, string> = {
  APPROVED:
    "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/40",
  SETTLED:
    "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/40",
  PENDING:
    "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/40",
  ACTION_REQUIRED:
    "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/40",
  DECLINED:
    "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-800/40",
  FAILED:
    "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-800/40",
  VOIDED: "bg-surface-muted text-muted ring-line",
  REFUNDED: "bg-surface-muted text-muted ring-line",
};

export function OrderStatusBadge({
  status,
  label,
  className,
}: {
  readonly status: OrderStatus;
  /** Pre-translated label. */
  readonly label: string;
  readonly className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
        ORDER_TONE[status],
        className,
      )}
    >
      {label}
    </span>
  );
}

export function PaymentStatusBadge({
  status,
  size = "default",
  className,
}: {
  readonly status: string;
  /** ``sm`` shrinks padding + font for dense rows (Timeline rail) where
   *  the badge sits beside an amount and would otherwise crowd the
   *  line. Default keeps the original shape for standalone use. */
  readonly size?: "sm" | "default";
  readonly className?: string;
}) {
  const tone = PAYMENT_TONE[status] ?? "bg-surface-strong text-foreground ring-line";
  const sizing =
    size === "sm"
      ? "px-1.5 py-0 text-[0.625rem] leading-tight tracking-wide"
      : "px-2 py-0.5 text-[0.6875rem] tracking-wide";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium uppercase ring-1 ring-inset",
        sizing,
        tone,
        className,
      )}
    >
      {status}
    </span>
  );
}
